import apiClient from './client';

export interface PortalCandidate {
  id: number;
  userId: number;
  name: string;
  email?: string;
  phone?: string;
  portalStatus: 'application_started' | 'application_submitted' | 'application_reviewed' | 
                'assessment_scheduled' | 'assessment_completed' | 'assessment_passed' | 
                'assessment_failed' | 'withdrawn';
  onboardingStep: number;
  onboardingCompleted: boolean;
  // Step-level completion flags (used for progress display)
  profileCompleted?: boolean;
  theologyCompleted?: boolean;
  referencesCompleted?: boolean;
  sendingChurchCompleted?: boolean;
  spiritualHealthCompleted?: boolean;
  spiritualHealthCandidateSubmitted?: boolean;
  spiritualHealthSpouseSubmitted?: boolean;
  // Split progress (backend-calculated)
  portalProgressPercentage?: number;
  teamProgressPercentage?: number;
  applicationSubmittedAt?: string;
  assessmentDate?: string;
  assessmentResults?: any;
  assessmentNotes?: string;
  migratedToChurchPlanterId?: number;
  migratedAt?: string;
  isActive: boolean;
  // Prepare/Enrich fields
  prepareEnrichRequested?: boolean;
  prepareEnrichRequestedAt?: string;
  prepareEnrichStatus?: 'not_started' | 'started' | 'scheduled' | 'complete';
  prepareEnrichStatusUpdatedAt?: string;
  prepareEnrichNotes?: string;
  prepareEnrichData?: any;
  // Background Check fields
  backgroundCheckSubmitted?: boolean;
  backgroundCheckSubmittedAt?: string;
  backgroundCheckStatus?: 'not_started' | 'started' | 'submitted' | 'received';
  backgroundCheckStatusUpdatedAt?: string;
  backgroundCheckNotes?: string;
  backgroundCheckData?: any;
  theologicalSurvey?: any;
  spiritualHealthSurveys?: any[];
  references?: any[];
  sendingChurch?: any;
  user?: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
    createdAt: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CandidateFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  onboardingStep?: number;
  onboardingCompleted?: boolean;
  sortBy?: string;
  order?: 'ASC' | 'DESC';
}

export interface OnboardingStats {
  total: number;
  completedOnboarding: number;
  inProgress: number;
  byStep: Record<string, number>;
  byStatus: Record<string, number>;
}

class PortalCandidateService {
  async getAllCandidates(filters: CandidateFilters = {}) {
    const response = await apiClient.get('/portal-candidates/admin/all', { params: filters });
    return response.data;
  }

  async getCandidate(id: number) {
    const response = await apiClient.get(`/portal-candidates/admin/${id}`);
    return response.data;
  }

  async updateCandidate(id: number, data: Partial<PortalCandidate>) {
    const response = await apiClient.put(`/portal-candidates/admin/${id}`, data);
    return response.data;
  }

  async updateTheologicalSurvey(id: number, data: any) {
    const response = await apiClient.put(`/portal-candidates/admin/${id}/theology`, data);
    return response.data;
  }

  async updateSpiritualHealthSurveys(id: number, data: any) {
    const response = await apiClient.put(`/portal-candidates/admin/${id}/spiritual-health`, data);
    return response.data;
  }

  async updateReferences(id: number, references: any[]) {
    const response = await apiClient.put(`/portal-candidates/admin/${id}/references`, { references });
    return response.data;
  }

  async updateSendingChurch(id: number, data: any) {
    const response = await apiClient.put(`/portal-candidates/admin/${id}/sending-church`, data);
    return response.data;
  }

  async deleteCandidate(id: number) {
    const response = await apiClient.delete(`/portal-candidates/admin/${id}`);
    return response.data;
  }

  async getOnboardingStats(): Promise<{ success: boolean; data: OnboardingStats }> {
    const response = await apiClient.get('/portal-candidates/admin/stats/onboarding');
    return response.data;
  }

  async scheduleAssessment(id: number, assessmentDate: string) {
    const response = await apiClient.post(`/portal-candidates/admin/${id}/schedule-assessment`, {
      assessmentDate,
    });
    return response.data;
  }

  async recordAssessmentResults(id: number, results: any, notes?: string, passed: boolean = false) {
    const response = await apiClient.post(`/portal-candidates/admin/${id}/assessment-results`, {
      results,
      notes,
      passed,
    });
    return response.data;
  }

  async sendCandidateDetails(id: number, recipientEmail: string) {
    const response = await apiClient.post(`/admin/portal-candidates/${id}/send-email`, {
      recipientEmail,
    });
    return response.data;
  }

  async downloadCandidatePdf(id: number, fallbackName?: string) {
    const response = await apiClient.get(`/admin/portal-candidates/${id}/pdf`, {
      responseType: 'blob',
      timeout: 60000,
    });

    const disposition: string | undefined = response.headers?.['content-disposition'];
    let filename = fallbackName ? `${fallbackName}.pdf` : `portal-candidate-${id}.pdf`;
    if (disposition) {
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      if (match && match[1]) {
        try {
          filename = decodeURIComponent(match[1]);
        } catch {
          filename = match[1];
        }
      }
    }

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  }
}

export const portalCandidateService = new PortalCandidateService();


